import { useEffect, useRef, useState } from 'react'
import { getBccSetting, setBccSetting, getFunFactsSetting, setFunFactsSetting, getMaintenanceModeSetting, setMaintenanceModeSetting, downloadBackup, restoreBackup, getAdminUserProfiles } from '../api/client'
import type { AdminUserProfile } from '../api/client'
import { getPendingEmails, approveEmail, rejectEmail } from '../api/users'
import type { PendingEmailOut } from '../api/users'
import { useStore } from '../store'
import { useTranslation } from 'react-i18next'

function UserProfilesOverview() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<AdminUserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    getAdminUserProfiles()
      .then(setUsers)
      .finally(() => setLoading(false))
  }, [])

  const toggle = (username: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(username)) {
        next.delete(username)
      } else {
        next.add(username)
      }
      return next
    })

  const totalProfiles = users.reduce((sum, u) => sum + u.profiles.length, 0)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('adminSettings.userProfiles.title')}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {loading
            ? t('adminSettings.userProfiles.summary_loading')
            : t('adminSettings.userProfiles.summary', {
                profileCount: totalProfiles,
                profileLabel: totalProfiles !== 1 ? t('adminSettings.userProfiles.profiles_plural') : t('adminSettings.userProfiles.profiles_singular'),
                userCount: users.filter(u => u.profiles.length > 0).length,
                userLabel: users.filter(u => u.profiles.length > 0).length !== 1 ? t('adminSettings.userProfiles.users_plural') : t('adminSettings.userProfiles.users_singular'),
              })}
        </p>
      </div>

      {loading ? (
        <div className="px-6 py-4 text-sm text-gray-400">{t('common.loading')}</div>
      ) : users.length === 0 ? (
        <div className="px-6 py-4 text-sm text-gray-400">{t('adminSettings.userProfiles.no_users')}</div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {users.map(u => {
            const isOpen = expanded.has(u.username)
            const activeProfile = u.profiles.find(p => p.is_active)
            return (
              <li key={u.username}>
                <button
                  type="button"
                  onClick={() => toggle(u.username)}
                  className="w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold flex items-center justify-center shrink-0">
                    {(u.initials ?? u.username.slice(0, 2)).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.username}</span>
                    {u.profiles.length === 0 ? (
                      <span className="ml-2 text-xs text-gray-400">{t('adminSettings.userProfiles.no_profiles')}</span>
                    ) : (
                      <span className="ml-2 text-xs text-gray-500">
                        {u.profiles.length} {u.profiles.length !== 1 ? t('adminSettings.userProfiles.profiles_plural') : t('adminSettings.userProfiles.profiles_singular')}
                        {activeProfile && <span className="ml-1.5 text-blue-600 font-medium">● {activeProfile.name} {t('adminSettings.userProfiles.active')}</span>}
                      </span>
                    )}
                  </div>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">{isOpen ? '▲' : '▼'}</span>
                </button>

                {isOpen && u.profiles.length > 0 && (
                  <div className="px-6 pb-4 space-y-2">
                    {u.profiles.map(p => (
                      <div
                        key={p.id}
                        className={`rounded-lg border px-4 py-3 text-xs space-y-1 ${p.is_active ? 'border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900'}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800 dark:text-gray-200">{p.name}</span>
                          {p.is_active && (
                            <span className="bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium">{t('adminSettings.userProfiles.active_badge')}</span>
                          )}
                        </div>
                        {p.project_number && <div className="text-gray-600 dark:text-gray-400">{t('adminSettings.userProfiles.project_number')} → <span className="font-mono">{p.project_number}</span></div>}
                        {(p.sender_name || p.sender_gln) && (
                          <div className="text-gray-600 dark:text-gray-400">
                            {t('adminSettings.userProfiles.sender')} → {p.sender_name && <span className="font-medium">{p.sender_name}</span>}
                            {p.sender_gln && <span className="font-mono ml-1">({p.sender_gln})</span>}
                          </div>
                        )}
                        {(p.receiver_name || p.receiver_gln) && (
                          <div className="text-gray-600 dark:text-gray-400">
                            {t('adminSettings.userProfiles.receiver')} → {p.receiver_name && <span className="font-medium">{p.receiver_name}</span>}
                            {p.receiver_gln && <span className="font-mono ml-1">({p.receiver_gln})</span>}
                          </div>
                        )}
                        {!p.project_number && !p.sender_gln && !p.sender_name && !p.receiver_gln && !p.receiver_name && (
                          <span className="italic text-gray-400">{t('adminSettings.userProfiles.no_replacements')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {isOpen && u.profiles.length === 0 && (
                  <div className="px-6 pb-4 text-xs text-gray-400 italic">{t('adminSettings.userProfiles.no_profiles_created')}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function PendingEmailsPanel() {
  const { t } = useTranslation()
  const [pending, setPending] = useState<PendingEmailOut[]>([])
  const [loading, setLoading] = useState(true)
  const [actionMsg, setActionMsg] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState<Record<number, string>>({})

  const load = () => {
    setLoading(true)
    getPendingEmails()
      .then(setPending)
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleApprove = async (emailId: number) => {
    await approveEmail(emailId)
    setActionMsg(t('adminSettings.pendingEmails.approved'))
    load()
  }

  const handleReject = async (emailId: number) => {
    await rejectEmail(emailId, rejectReason[emailId] || undefined)
    setActionMsg(t('adminSettings.pendingEmails.rejected'))
    load()
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('adminSettings.pendingEmails.title')}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{t('adminSettings.pendingEmails.subtitle')}</p>
      </div>
      {actionMsg && (
        <div className="px-6 py-2 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20">
          {actionMsg} <button className="ml-2 underline" onClick={() => setActionMsg(null)}>✕</button>
        </div>
      )}
      {loading ? (
        <div className="px-6 py-4 text-sm text-gray-400">{t('common.loading')}</div>
      ) : pending.length === 0 ? (
        <div className="px-6 py-4 text-sm text-gray-400 italic">{t('adminSettings.pendingEmails.empty')}</div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {pending.map(p => (
            <li key={p.email_id} className="px-6 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">{p.email}</span>
                {p.label && <span className="ml-1.5 text-gray-400">({p.label})</span>}
                <span className="ml-2 text-xs text-gray-500">{t('adminSettings.pendingEmails.user')}: <span className="font-medium">{p.username}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={rejectReason[p.email_id] ?? ''}
                  onChange={e => setRejectReason(prev => ({ ...prev, [p.email_id]: e.target.value }))}
                  placeholder={t('adminSettings.pendingEmails.reason_placeholder')}
                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-36 dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  onClick={() => handleApprove(p.email_id)}
                  className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-medium transition-colors"
                >
                  {t('adminSettings.pendingEmails.btn_approve')}
                </button>
                <button
                  onClick={() => handleReject(p.email_id)}
                  className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded font-medium transition-colors"
                >
                  {t('adminSettings.pendingEmails.btn_reject')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function AdminSettings() {
  const { t } = useTranslation()
  const { setError } = useStore()
  const [bcc, setBcc] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  const [funFactsEnabled, setFunFactsEnabled] = useState(false)
  const [funFactsLoading, setFunFactsLoading] = useState(true)
  const [funFactsSaving, setFunFactsSaving] = useState(false)

  const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState(false)
  const [maintenanceModeLoading, setMaintenanceModeLoading] = useState(true)
  const [maintenanceModeSaving, setMaintenanceModeSaving] = useState(false)

  const [backupLoading, setBackupLoading] = useState(false)
  const [restoreFile, setRestoreFile] = useState<File | null>(null)
  const [restoreLoading, setRestoreLoading] = useState(false)
  const [restoreConfirm, setRestoreConfirm] = useState(false)
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getBccSetting()
      .then(setBcc)
      .catch(() => setError(t('adminSettings.bcc.load_error')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    getFunFactsSetting()
      .then(setFunFactsEnabled)
      .catch(() => setError(t('adminSettings.funFacts.load_error')))
      .finally(() => setFunFactsLoading(false))
  }, [])

  const handleFunFactsToggle = async (checked: boolean) => {
    setFunFactsSaving(true)
    try {
      const saved = await setFunFactsSetting(checked)
      setFunFactsEnabled(saved)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adminSettings.funFacts.save_error'))
    } finally {
      setFunFactsSaving(false)
    }
  }

  useEffect(() => {
    getMaintenanceModeSetting()
      .then(setMaintenanceModeEnabled)
      .catch(() => setError(t('adminSettings.maintenanceMode.load_error')))
      .finally(() => setMaintenanceModeLoading(false))
  }, [])

  const handleMaintenanceModeToggle = async (checked: boolean) => {
    setMaintenanceModeSaving(true)
    try {
      const saved = await setMaintenanceModeSetting(checked)
      setMaintenanceModeEnabled(saved)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adminSettings.maintenanceMode.save_error'))
    } finally {
      setMaintenanceModeSaving(false)
    }
  }

  const handleBackup = async () => {
    setBackupLoading(true)
    try {
      await downloadBackup()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adminSettings.backup.download_error'))
    } finally {
      setBackupLoading(false)
    }
  }

  const handleRestore = async () => {
    if (!restoreFile) return
    setRestoreLoading(true)
    setRestoreMsg(null)
    try {
      const result = await restoreBackup(restoreFile)
      setRestoreMsg(result.message)
      setRestoreConfirm(false)
      setRestoreFile(null)
      if (fileRef.current) fileRef.current.value = ''
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adminSettings.restore.error'))
    } finally {
      setRestoreLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setSavedMsg(null)
    try {
      const saved = await setBccSetting(bcc.trim())
      setBcc(saved)
      setSavedMsg(saved ? t('adminSettings.bcc.saved_active', { email: saved }) : t('adminSettings.bcc.saved_disabled'))
      setTimeout(() => setSavedMsg(null), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adminSettings.bcc.save_error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Backup */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('adminSettings.backup.title')}</h3>
          <p className="text-xs text-gray-500">
            {t('adminSettings.backup.description')}
          </p>
        </div>
        <button
          onClick={handleBackup}
          disabled={backupLoading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-50 transition-colors"
        >
          {backupLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              {t('adminSettings.backup.preparing')}
            </>
          ) : t('adminSettings.backup.download_button')}
        </button>
      </div>

      {/* Restore */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800/50 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('adminSettings.restore.title')}</h3>
          <p className="text-xs text-gray-500">
            {t('adminSettings.restore.description_prefix')} <span className="font-semibold text-red-600">{t('adminSettings.restore.description_warning')}</span>{' '}
            {t('adminSettings.restore.description_suffix')}
          </p>
        </div>

        <div className="space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept=".zip"
            onChange={e => {
              setRestoreFile(e.target.files?.[0] ?? null)
              setRestoreConfirm(false)
              setRestoreMsg(null)
            }}
            className="text-sm text-gray-600"
          />

          {restoreFile && !restoreConfirm && (
            <button
              onClick={() => setRestoreConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              {t('adminSettings.restore.restore_with_file')} <span className="font-mono">{restoreFile.name}</span>
            </button>
          )}

          {restoreConfirm && (
            <div className="rounded-lg border border-red-300 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-4 space-y-3">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                {t('adminSettings.restore.confirm_question')}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleRestore}
                  disabled={restoreLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {restoreLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      {t('adminSettings.restore.restoring')}
                    </>
                  ) : t('adminSettings.restore.confirm_button')}
                </button>
                <button
                  onClick={() => setRestoreConfirm(false)}
                  className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {restoreMsg && (
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
              <span className="text-amber-500 mt-0.5">⚠</span>
              <span>{restoreMsg}</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('adminSettings.bcc.title')}</h3>
          <p className="text-xs text-gray-500">
            {t('adminSettings.bcc.description')}
          </p>
        </div>

        {loading ? (
          <div className="text-xs text-gray-400">{t('common.loading')}</div>
        ) : (
          <div className="flex gap-2">
            <input
              type="email"
              value={bcc}
              onChange={e => setBcc(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              placeholder="admin@example.com"
              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-blue-400 focus:border-indigo-400 dark:bg-gray-800 dark:text-gray-100"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? t('adminSettings.bcc.saving') : t('common.save')}
            </button>
          </div>
        )}

        {savedMsg && (
          <p className="text-xs text-green-600 font-medium">✓ {savedMsg}</p>
        )}

        {bcc && !loading && (
          <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
            <span className="text-amber-500">●</span>
            {t('adminSettings.bcc.active_notice')} <span className="font-mono font-medium">{bcc}</span>
          </div>
        )}
      </div>

      {/* Fun facts */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('adminSettings.funFacts.title')}</h3>
          <p className="text-xs text-gray-500">
            {t('adminSettings.funFacts.description')}
          </p>
        </div>

        {funFactsLoading ? (
          <div className="text-xs text-gray-400">{t('common.loading')}</div>
        ) : (
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={funFactsEnabled}
              disabled={funFactsSaving}
              onChange={e => handleFunFactsToggle(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('adminSettings.funFacts.toggle_label')}</span>
          </label>
        )}
      </div>

      {/* Onderhoudsmodus */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('adminSettings.maintenanceMode.title')}</h3>
          <p className="text-xs text-gray-500">
            {t('adminSettings.maintenanceMode.description')}
          </p>
        </div>

        {maintenanceModeLoading ? (
          <div className="text-xs text-gray-400">{t('common.loading')}</div>
        ) : (
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={maintenanceModeEnabled}
              disabled={maintenanceModeSaving}
              onChange={e => handleMaintenanceModeToggle(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('adminSettings.maintenanceMode.toggle_label')}</span>
          </label>
        )}

        {maintenanceModeEnabled && (
          <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
            <span className="text-amber-500">●</span>
            {t('adminSettings.maintenanceMode.active_notice')}
          </div>
        )}
      </div>

      <PendingEmailsPanel />
      <UserProfilesOverview />
    </div>
  )
}
